import { useState } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useAuth } from "@clerk/clerk-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "@base-ui/react/dialog";
import { Input } from "@base-ui/react/input";
import { Field } from "@base-ui/react/field";
import { Select } from "@base-ui/react/select";
import { Combobox } from "@base-ui/react/combobox";
import { Tooltip } from "@base-ui/react/tooltip";
import {
  IconPlus,
  IconTrash,
  IconUsers,
  IconChevronDown,
  IconCheck,
} from "@tabler/icons-react";
import {
  invitesQueryOptions,
  positionsQueryOptions,
} from "../../../api/queries";
import { createInvite, deleteInvite } from "../../../api/invites";
import { parseOrgId } from "../../../lib/slug";
import type { PositionSummary } from "../../../api/positions";

export const Route = createFileRoute("/organization/$slug/members")({
  component: Members,
});

const EXPIRY_OPTIONS = [
  { label: "1 hour", value: 60 * 60 },
  { label: "4 hours", value: 4 * 60 * 60 },
  { label: "1 day", value: 24 * 60 * 60 },
  { label: "7 days", value: 7 * 24 * 60 * 60 },
  { label: "14 days", value: 14 * 24 * 60 * 60 },
];

function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString();
}

function Members() {
  const { getToken } = useAuth();
  const { slug } = useParams({ from: "/organization/$slug" });
  const orgId = parseOrgId(slug);
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState<PositionSummary | null>(null);
  const [expirySeconds, setExpirySeconds] = useState<
    (typeof EXPIRY_OPTIONS)[number]
  >(EXPIRY_OPTIONS.find((o) => o.label === "7 days")!);

  const { data: invitesData, isLoading } = useQuery(
    invitesQueryOptions(getToken, orgId)
  );
  const invites = invitesData?.invites ?? [];

  const { data: positionsData } = useQuery(
    positionsQueryOptions(getToken, orgId)
  );
  const positions = positionsData?.positions ?? [];

  const createMutation = useMutation({
    mutationFn: () =>
      createInvite(getToken, orgId, {
        name: name.trim(),
        email: email.trim(),
        positionId: position!.id,
        expiresAt: Math.floor(Date.now() / 1000) + expirySeconds.value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invites", orgId] });
      setName("");
      setEmail("");
      setPosition(null);
      setExpirySeconds(EXPIRY_OPTIONS.find((o) => o.label === "7 days")!);
      setDialogOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (inviteId: string) => deleteInvite(getToken, orgId, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invites", orgId] });
    },
  });

  function handleOpenChange(open: boolean) {
    setDialogOpen(open);
    if (!open) {
      setName("");
      setEmail("");
      setPosition(null);
      setExpirySeconds(EXPIRY_OPTIONS.find((o) => o.label === "7 days")!);
      createMutation.reset();
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-darker">Members</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your organization's members and pending invitations.
          </p>
        </div>

        <Dialog.Root open={dialogOpen} onOpenChange={handleOpenChange}>
          <Dialog.Trigger className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-darker transition-colors">
            <IconPlus size={16} />
            Invite Member
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Backdrop className="fixed inset-0 bg-black/40" />
            <Dialog.Popup className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Invite Member
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500">
                Send an email invitation to a new member.
              </Dialog.Description>

              <form
                className="mt-4 space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (name.trim() && email.trim() && position) {
                    createMutation.mutate();
                  }
                }}
              >
                <Field.Root>
                  <Field.Label className="block text-sm font-medium text-gray-700">
                    Name
                  </Field.Label>
                  <Input
                    required
                    value={name}
                    onValueChange={setName}
                    placeholder="e.g. Jane Smith"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label className="block text-sm font-medium text-gray-700">
                    Email
                  </Field.Label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onValueChange={setEmail}
                    placeholder="jane@example.com"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label className="block text-sm font-medium text-gray-700">
                    Position
                  </Field.Label>
                  <Combobox.Root
                    value={position}
                    onValueChange={setPosition}
                    items={positions}
                    itemToStringLabel={(p) => p.name}
                  >
                    <div className="relative mt-1">
                      <Combobox.Input
                        placeholder="Search positions…"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <Combobox.Trigger className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                        <IconChevronDown size={16} />
                      </Combobox.Trigger>
                    </div>
                    <Combobox.Portal>
                      <Combobox.Positioner sideOffset={4}>
                        <Combobox.Popup className="z-50 min-w-[var(--anchor-width)] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                          <Combobox.Empty>
                            <div className="px-3 py-2 text-sm text-gray-400">
                              No positions found
                            </div>
                          </Combobox.Empty>
                          <Combobox.List>
                            {(p: PositionSummary) => (
                              <Combobox.Item
                                key={p.id}
                                value={p}
                                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-900 data-[highlighted]:bg-gray-100"
                              >
                                <Combobox.ItemIndicator className="inline-flex">
                                  <IconCheck size={14} />
                                </Combobox.ItemIndicator>
                                {p.name}
                              </Combobox.Item>
                            )}
                          </Combobox.List>
                        </Combobox.Popup>
                      </Combobox.Positioner>
                    </Combobox.Portal>
                  </Combobox.Root>
                </Field.Root>

                <Field.Root>
                  <Field.Label className="block text-sm font-medium text-gray-700">
                    Expires in
                  </Field.Label>
                  <Select.Root
                    items={EXPIRY_OPTIONS}
                    value={expirySeconds.value}
                    onValueChange={(v) =>
                      setExpirySeconds(
                        EXPIRY_OPTIONS.find((o) => o.value === Number(v))!
                      )
                    }
                  >
                    <Select.Trigger className="mt-1 flex w-full items-center justify-between rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary">
                      <Select.Value />
                      <Select.Icon>
                        <IconChevronDown size={16} className="text-gray-400" />
                      </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Positioner sideOffset={4}>
                        <Select.Popup className="z-50 min-w-[var(--anchor-width)] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                          {EXPIRY_OPTIONS.map((o) => (
                            <Select.Item
                              key={o.value}
                              value={o.value}
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-900 data-[highlighted]:bg-gray-100"
                            >
                              <Select.ItemIndicator className="inline-flex">
                                <IconCheck size={14} />
                              </Select.ItemIndicator>
                              <Select.ItemText>{o.label}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Popup>
                      </Select.Positioner>
                    </Select.Portal>
                  </Select.Root>
                </Field.Root>

                {createMutation.isError && (
                  <p className="text-sm text-red-600">
                    Failed to send invite. Please try again.
                  </p>
                )}

                <div className="flex justify-end gap-2">
                  <Dialog.Close className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    Cancel
                  </Dialog.Close>
                  <button
                    type="submit"
                    disabled={
                      !name.trim() ||
                      !email.trim() ||
                      !position ||
                      createMutation.isPending
                    }
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-darker disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {createMutation.isPending ? "Sending…" : "Send Invite"}
                  </button>
                </div>
              </form>
            </Dialog.Popup>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      {/* Outstanding invites */}
      <div className="mt-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">
          Outstanding Invitations
        </h2>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading invitations…</p>
        ) : invites.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-12">
            <IconUsers size={40} stroke={1.5} className="text-gray-400" />
            <p className="mt-3 text-sm font-medium text-gray-900">
              No pending invitations
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Invite a new member using the button above.
            </p>
          </div>
        ) : (
          <table className="w-full rounded-xl border border-gray-200 bg-white text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Position</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr
                  key={invite.inviteId}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {invite.name}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{invite.email}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {invite.position?.name ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        invite.status === "INVITED"
                          ? "bg-blue-100 text-blue-700"
                          : invite.status === "REVOKED"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {invite.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {formatDate(invite.expiresAt)}
                  </td>
                  <td className="px-4 py-3">
                    {invite.status !== "REVOKED" && (
                      <Tooltip.Provider>
                        <Tooltip.Root>
                          <Tooltip.Trigger
                            onClick={() => deleteMutation.mutate(invite.inviteId)}
                            disabled={deleteMutation.isPending}
                            className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                          >
                            <IconTrash size={16} />
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Positioner sideOffset={6}>
                              <Tooltip.Popup className="rounded-md bg-gray-900 px-2 py-1 text-xs text-white">
                                Revoke invitation
                              </Tooltip.Popup>
                            </Tooltip.Positioner>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </Tooltip.Provider>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
